/**
 * NotificationsList — the "Notifications" tab of the Mobile Forensics view.
 *
 * The OS's own notification record (ADR-0018): what was scheduled to fire or
 * sitting on screen when the backup ran, newest-first and grouped by day, plus
 * the inventory of applications registered for notifications — which survives
 * even for applications whose content is unobtainable (Signal, Messenger).
 * Evidence of activity, never recovered messages, and the copy must keep the
 * two apart: the store is a snapshot, so an empty list says nothing about what
 * was received.
 */

import { ArrowDownIcon, BellIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { type FC, Fragment, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterChip } from '@/components/ui/filter-chip'
import { SearchInput } from '@/components/ui/search-input'
import type { CommsNotificationEvent } from '@/services/api/comms'
import { bubbleTime, bundleAppLabel, dayLabel } from './comms-format'

interface NotificationsListProps {
  events: CommsNotificationEvent[]
  /** Bundle ids the OS had registered for notifications — first page only. */
  registered: string[]
  /** Distinct apps behind the timed events — drives the filter chips. */
  apps: string[]
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
}

const KIND_DELIVERED = 'notification.delivered'

const EventRow = memo(function EventRow({ event }: { event: CommsNotificationEvent }) {
  const { t, i18n } = useTranslation()
  const kind = event.kind.split('.').pop() ?? event.kind
  return (
    <div className="flex items-center gap-3 border-border/40 border-b px-3 py-2 max-md:items-start">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <BellIcon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        {/* The text is the evidence: one line shows it whole on a desktop, a
            phone needs up to three. */}
        <p className="text-sm text-foreground max-md:line-clamp-3 max-md:break-words md:truncate">
          {event.detail ||
            (event.app ? bundleAppLabel(event.app, t) : t('comms.notifications.unattributed'))}
        </p>
        {/* The bundle id verbatim — the attribution is the evidence. */}
        <p className="truncate font-mono text-2xs text-muted-foreground">
          {event.app ?? t('comms.notifications.unattributed')}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="font-mono text-2xs text-muted-foreground">
          {bubbleTime(event.occurredAt, i18n.language)}
        </span>
        <Badge
          variant={event.kind === KIND_DELIVERED ? 'secondary' : 'outline'}
          className="h-4 px-1 text-2xs"
        >
          {t(`comms.notifications.kind.${kind}`, { defaultValue: kind })}
        </Badge>
      </div>
    </div>
  )
})

const DaySeparator: FC<{ iso: string }> = ({ iso }) => {
  const { i18n } = useTranslation()
  return (
    <div className="bg-muted/40 px-3 py-1 font-medium text-2xs text-muted-foreground uppercase tracking-wide">
      {dayLabel(iso, i18n.language)}
    </div>
  )
}

/** The registration inventory, collapsed by default — for an application like
 * Signal it is the only notification evidence there is, so it must be present
 * even when no timed event survived. */
const RegisteredApps: FC<{ registered: string[]; needle: string }> = ({ registered, needle }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const visible = useMemo(
    () =>
      needle
        ? registered.filter(id => `${id} ${bundleAppLabel(id, t)}`.toLowerCase().includes(needle))
        : registered,
    [registered, needle, t]
  )
  if (registered.length === 0) return null
  const Chevron = open ? ChevronUpIcon : ChevronDownIcon
  return (
    <div className="border-border/40 border-b">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-muted-foreground text-xs transition-colors hover:text-foreground"
      >
        <BellIcon className="size-3.5" />
        {t('comms.notifications.registered', { count: registered.length })}
        <Chevron className="size-3.5" />
      </button>
      {open && (
        <div className="flex max-h-44 flex-wrap gap-1 overflow-y-auto px-3 pb-2">
          {visible.map(id => (
            // Phone: a bundle id longer than the screen wraps inside its badge
            // rather than making the box scroll sideways.
            <Badge
              key={id}
              variant="outline"
              className="h-5 px-1.5 font-mono text-2xs max-md:h-auto max-md:min-h-5 max-md:max-w-full max-md:whitespace-normal max-md:break-all max-md:rounded-xl"
            >
              {id}
            </Badge>
          ))}
          {visible.length === 0 && (
            <p className="py-1 text-muted-foreground text-xs">
              {t('comms.notifications.registeredEmpty')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export const NotificationsList: FC<NotificationsListProps> = ({
  events,
  registered,
  apps,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [app, setApp] = useState<string | null>(null)

  const needle = search.trim().toLowerCase()
  const visible = useMemo(
    () =>
      events.filter(event => {
        if (app && event.app !== app) return false
        if (!needle) return true
        return `${event.detail ?? ''} ${event.app ?? ''}`.toLowerCase().includes(needle)
      }),
    [events, app, needle]
  )

  // Day boundaries over the filtered, newest-first window — computed once.
  const rows = useMemo(() => {
    let prevDay = ''
    return visible.map(event => {
      const day = event.occurredAt ? new Date(event.occurredAt).toDateString() : ''
      const newDay = day !== prevDay
      prevDay = day
      return { event, newDay: newDay && !!event.occurredAt }
    })
  }, [visible])

  return (
    // Phone: the search, the hint, the chips and the inventory scroll away with
    // the rows — pinned, they would leave the list a sliver of the screen.
    <div className="flex h-full min-h-0 flex-col max-md:block max-md:overflow-y-auto">
      <div className="flex flex-wrap items-center gap-2 px-3 pt-3 pb-2">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder={t('comms.notifications.searchPlaceholder')}
          containerClassName="h-9 min-w-56 flex-1 md:h-8"
        />
      </div>
      {/* A snapshot, not a log — without this line the timed rows read as
          "everything the device received". */}
      <p className="px-3 pb-2 text-2xs text-muted-foreground">{t('comms.notifications.hint')}</p>
      {apps.length > 1 && (
        <div className="flex flex-wrap gap-1 px-3 pb-2">
          <FilterChip
            label={t('comms.list.allApps')}
            count={events.length}
            active={app === null}
            onClick={() => setApp(null)}
          />
          {apps.map(a => (
            <FilterChip
              key={a}
              label={bundleAppLabel(a, t)}
              count={events.filter(e => e.app === a).length}
              active={app === a}
              onClick={() => setApp(a)}
            />
          ))}
        </div>
      )}
      <RegisteredApps registered={registered} needle={needle} />
      <div className="min-h-0 flex-1 md:overflow-y-auto">
        {rows.map(({ event, newDay }) => (
          <Fragment key={event.eventId}>
            {newDay && event.occurredAt && <DaySeparator iso={event.occurredAt} />}
            <EventRow event={event} />
          </Fragment>
        ))}
        {visible.length === 0 && (
          <p className="px-3 py-6 text-center text-muted-foreground text-xs">
            {t('comms.notifications.empty')}
          </p>
        )}
        {hasMore && (
          <div className="flex justify-center p-3">
            <Button variant="outline" size="sm" onClick={onLoadMore} loading={loadingMore}>
              {!loadingMore && <ArrowDownIcon className="size-3.5" />}
              {t('comms.notifications.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
